import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import useItem from "common/hooks/useItem";
import useRouter from "common/hooks/use-router";
import DetailHeader from "common/components/DetailHeader/DetailHeader";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import PageLoading from "common/components/PageLoading/PageLoading";
import Icon from "components/Icon/Icon";
import Status from "common/components/Status/Status";
import AlertsContext from "common/providers/alerts";
import { Report } from "types/Report";
import Button from "common/components/Button/Button";
import ProfileCard from "components/ProfileCard/ProfileCard";

function ReportDetails() {
  const router = useRouter();
  const id = router.query.id as string;
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const { item, isFetching, updateItem, error }: any = useItem("reports", id, {
    populate: [
      "user",
      "userProfile",
      "userProfile.avatar",
      "userProfile.companyLogo",
      "author",
      "authorProfile",
      "authorProfile.avatar",
      "authorProfile.companyLogo",
      "content",
      "content.authorProfile",
      "content.authorProfile.avatar",
      "content.authorProfile.companyLogo",
      "content.images",
      "conversation",
      "conversation.content",
      "conversation.content.images",
      "conversation.content.author",
      "conversation.content.author.avatar",
      "conversation.content.author.companyLogo",
      "conversation.providerProfile",
      "conversation.providerProfile.avatar",
      "conversation.applicantProfile",
      "conversation.applicantProfile.avatar",
    ],
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const {
    content,
    user,
    userProfile,
    conversation,
    authorProfile,
    description,
    state,
  } = (item as Report) || {};

  const typeContent: any = useMemo(() => {
    if (item?.status === "conversation") {
      return {
        name: t('common.conversation'),
        icon: 'ChatBubbleLeftRightIcon',
        label: item?.conversation?.providerProfile?.username,
        key: 'Conversation',
        foreignKey: 'Conversation',
        url: `/conversations/${conversation?.id}`,
      };
    }
    if (item?.status === "content") {
      return {
        name: t("common.content"),
        icon: "CalendarDaysIcon",
        label: item.content.title,
        key: "Annonce",
        foreignKey: "Content",
        url: `/contents/${content?.id}`,
      };
    }
    if (item?.status === "user") {
      return {
        name: t("common.profile"),
        icon: "UserIcon",
        label: item.userProfile.username,
        key: "Utilisateur",
        foreignKey: "User",
        url: `/profiles/edit/${userProfile?.id}`,
      };
    }
    return null;
  }, [content, item, t, conversation, userProfile]);

  const canProcess = state === "unprocessed";
  const canUnprocess = state === "processed";

  const ReportHeaderInfos = useMemo(
    () => (
      <div className="w-full">
        <div className="flex items-center gap-1 field-title">
          <Icon name={typeContent?.icon} className="w-4 h-4" />
          <span>
            {t("reports.reportedItemOfType", { item: typeContent?.name })}
          </span>
        </div>
      </div>
    ),
    [t, typeContent]
  );

  const handleProcess = useCallback(() => {
    updateItem(id, { state: "processed" });
  }, [id, updateItem]);

  const handleUnprocess = useCallback(() => {
    updateItem(id, { state: "unprocessed" });
  }, [id, updateItem]);

  if (isFetching && !item) {
    return <PageLoading />;
  }

  return !item ? null : (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
      <DetailHeader
        backLink="/reports"
        title={t("titlePages.reports")}
        TitleIcon={
          <div>
            <Icon
              name="ExclamationTriangleIcon"
              className="w-16 h-16 mr-1 text-primary"
            />
          </div>
        }
        Status={
          <div className="flex gap-2 flex-wrap">
            <Status status={state} />
          </div>
        }
        HeaderInfos={ReportHeaderInfos}
        HeaderButtons={
          <div className="responsive-flex-lg gap-1 lg:justify-end w-full mt-3 lg:mt-0 items-start lg:items-center">
            {canProcess && (
              <Button
                title={t("actions.process")}
                type="valid"
                icon="CheckIcon"
                onClick={handleProcess}
                // compact
              />
            )}
            {canUnprocess && (
              <Button
                title={t("actions.unprocess")}
                type="error-line"
                icon="MdOutlineUnpublished"
                onClick={handleUnprocess}
                // compact
              />
            )}
          </div>
        }
      />

      <div className="responsive-flex gap-2 mt-2">
        <div className="flex-1">
          <div className="flex flex-col gap-2 w-full">
            {/* Description */}
            <div className="responsive-flex flex flex-col lg:flex-row gap-4 items-start">
              {/* Column 1 */}
              <div className="responsive-flex gap-4 flex-col w-full lg:basis-1/2">
                <div className="white-box flex-col w-full">
                  <SectionTitle
                    title={t(`reports.reported${typeContent?.foreignKey}`)}
                    icon="ExclamationTriangleIcon"
                    className="text-secondary mb-4"
                  />
                  {userProfile && (
                    <ProfileCard
                      profile={userProfile}
                      noTitle
                      onClick={() => {
                        if (userProfile?.id) {
                          router.navigate(`/profiles/edit/${userProfile.id}`);
                        }
                      }}
                      userEmail={user?.email}
                    />
                  )}
                </div>

                <div className="white-box flex-col w-full">
                  <SectionTitle
                    title={t("reports.reason")}
                    icon="BookmarkIcon"
                    className="text-secondary"
                  />
                  <div className="responsive-flex py-4 flex-row">
                    {description ? (
                      description.split("\n").map((p: any, index: number) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <p key={`p-${index}`} className="mb-4">
                          {p}
                        </p>
                      ))
                    ) : (
                      <p>{t("forms.noContent")}</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Column 2 */}
              <div className="white-box responsive-flex gap-4 flex-col w-full lg:basis-1/2">
                <SectionTitle
                  title={t("forms.author")}
                  icon="UserIcon"
                  className="text-ternary"
                />
                {authorProfile ? (
                  <ProfileCard
                    profile={authorProfile}
                    noTitle
                    onClick={() => {
                      if (authorProfile?.id) {
                        router.navigate(`/profiles/edit/${authorProfile.id}`);
                      }
                    }}
                  />
                ) : (
                  <div className="font-bold text-lg">Auteur non renseigné</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportDetails;
